import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { useMutation } from '@ubisend/pulse-hooks';
import {
  Label,
  Flex,
  Button,
  useNotification,
  useModal
} from '@ubisend/pulse-components';
import { useConditionalReducer } from '@ubisend/pulse-conditionals';

import Modal from '../../../Modal';
import { conditionals as conditionalSubjects } from '../../../../../../subjects/index';
import { useBuilder } from '../../../../hooks/index';
import {
  updateStepTransition as updateStepTransitionApi,
  updateStepTrigger as updateStepTriggerApi
} from '../../../../api/index';
import MetricSelect from '../../../MetricSelect';

const EditMetricBlock = ({ handleClose, node, block }) => {
  const [metric, setMetric] = useState(block.base.id);

  const { dispatch } = useBuilder();

  const { showModal, hideModal } = useModal();

  const { showSuccess } = useNotification();

  const conditionals = useConditionalReducer({
    subjects: conditionalSubjects,
    conditionals: {
      match: node.base.match,
      conditionals: node.base.conditionals
    }
  });

  const updateStepTransitionMutation = useMutation({
    mutationFn: updateStepTransitionApi,
    onSuccess: ({ data }) => {
      showSuccess('Successfully updated metrics');
      dispatch({
        type: 'UPDATE_TRANSITION',
        stepId: node.meta.stepId,
        transitionId: node.id,
        transitionDetails: data.data,
        transitionType: node.type
      });
      handleClose();
    }
  });

  const updateStepTriggerMutation = useMutation({
    mutationFn: updateStepTriggerApi,
    onSuccess: ({ data }) => {
      showSuccess('Successfully updated metrics');
      dispatch({
        type: 'UPDATE_TRIGGER',
        stepId: node.meta.stepId,
        triggerId: node.id,
        triggerDetails: data.data
      });
      handleClose();
    }
  });

  const handleMetricChange = metric => {
    setMetric(metric ? metric.value : null);
  };

  const valid = useMemo(() => {
    return Boolean(metric);
  }, [metric]);

  const isLoading = useMemo(() => {
    return updateStepTriggerMutation.isPending || updateStepTransitionMutation.isPending;
  }, [updateStepTriggerMutation.isPending, updateStepTransitionMutation.isPending]);

  const updateTransitionMetrics = callback => {
    const getDescription = () => {
      if (node.type === 'validation') {
        return `Validation for step #${node.base.destination.id}`;
      }

      return `Transition to step #${node.base.destination.id}`;
    };

    return {
      stepId: node.meta.stepId,
      transitionId: node.id,
      transition: {
        description: getDescription(),
        transition: {
          id: node.base.destination.id,
          ...conditionals.form,
          variables: node.base.variables.map(variable => variable.id),
          metrics: callback(node.base.metrics)
        }
      }
    };
  };

  const updateTriggerMetrics = callback => {
    return {
      stepId: node.meta.stepId,
      triggerId: node.id,
      trigger: {
        description: `Trigger step #${node.meta.stepId}`,
        ...conditionals.form,
        metrics: callback(node.base.metrics),
        variables: node.base.variables.map(variable => variable.id)
      }
    };
  };

  const handleTriggerUpdate = () => {
    if (valid && !isLoading) {
      updateStepTriggerMutation.mutate(
        updateTriggerMetrics(metrics => {
          return metrics.map(({ id }) => {
            if (id === block.base.id) {
              return metric;
            }

            return id;
          });
        })
      );
    }
  };

  const handleTransitionUpdate = () => {
    if (valid && !isLoading) {
      updateStepTransitionMutation.mutate(
        updateTransitionMetrics(metrics => {
          return metrics.map(({ id }) => {
            if (id === block.base.id) {
              return metric;
            }

            return id;
          });
        })
      );
    }
  };

  const handleSubmit = event => {
    event.preventDefault();

    if (node.type === 'trigger') {
      handleTriggerUpdate();
    } else {
      handleTransitionUpdate();
    }
  };

  const handleTriggerDelete = () => {
    return updateStepTriggerMutation.mutateAsync(
      updateTriggerMetrics(metrics => {
        return metrics
          .filter(({ id }) => id !== block.base.id)
          .map(({ id }) => id);
      })
    );
  };

  const handleTransitionDelete = () => {
    return updateStepTransitionMutation.mutateAsync(
      updateTransitionMetrics(metrics => {
        return metrics
          .filter(({ id }) => id !== block.base.id)
          .map(({ id }) => id);
      })
    );
  };

  const handleDelete = () => {
    showModal({
      header: 'Remove metric',
      body: `Are you sure you want to remove this metric?`,
      handleConfirm: async () => {
        try {
          if (node.type === 'trigger') {
            await handleTriggerDelete();
          } else {
            await handleTransitionDelete();
          }
        } finally {
          hideModal();
        }
      }
    });
  };

  const getSelectedMetrics = () => {
    return node.blocks?.filter(block => block.type === 'metric'); // eslint-disable-line
  };

  return (
    <Modal
      header="Edit metric"
      handleClose={handleClose}
      actions={
        <Button
          variant="secondary"
          colour="danger"
          icon="trash"
          onClick={handleDelete}
          loading={isLoading}>
          Delete
        </Button>
      }>
      <form onSubmit={handleSubmit}>
        <Flex col pad="true">
          <Flex col mb>
            <Label htmlFor="metric">Metric</Label>
            <MetricSelect
              id="metric"
              value={metric}
              onChange={handleMetricChange}
              currentlySelected={getSelectedMetrics()}
            />
          </Flex>
          <Flex right>
            <Button
              variant="primary"
              type="submit"
              loading={isLoading}
              disabled={isLoading || !valid}>
              Save
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

EditMetricBlock.propTypes = {
  handleClose: PropTypes.func.isRequired,
  node: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    meta: PropTypes.shape({
      stepId: PropTypes.number.isRequired
    }).isRequired,
    base: PropTypes.shape({
      match: PropTypes.string.isRequired,
      conditionals: PropTypes.array.isRequired,
      destination: PropTypes.shape({
        id: PropTypes.number.isRequired
      }).isRequired,
      metrics: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired
      ).isRequired,
      variables: PropTypes.arrayOf(
        PropTypes.shape({ id: PropTypes.number.isRequired }).isRequired
      ).isRequired
    }).isRequired
  }).isRequired,
  block: PropTypes.shape({
    base: PropTypes.shape({
      id: PropTypes.number.isRequired
    }).isRequired
  }).isRequired
};

export default EditMetricBlock;
